import React, { useMemo, useState, useEffect } from "react";
import "./CreateClinicalNote.css";

import LocationForm from "./CreateClinicalNoteComp/LocationForm";
import VitalsForm from "./CreateClinicalNoteComp/VitalsForm";
import HpiForm from "./CreateClinicalNoteComp/HpiForm";
import RosForm from "./CreateClinicalNoteComp/RosForm";
import ExamForm from "./CreateClinicalNoteComp/ExamForm";
import Phq2Form from "./CreateClinicalNoteComp/Phq2Form";
import AssessmentsPlanFollowUp from "./CreateClinicalNoteComp/AssessmentsPlanFollowUp";
import ActionsBar from "./CreateClinicalNoteComp/ActionsBar";

// Builder (se queda igual)
import buildEhrNotePayload from "../../utils/services/APP/ClinicalNotes/buildEhrNotePayload";
// NUEVO: servicio que pega a TU backend
import { addEhrNote } from "../../utils/services/AdvancedMD/ehrNotes";

const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const toUsDatetime = (d) => {
  const h = d.getHours();
  const hour12 = ((h + 11) % 12) + 1;
  const ampm = h >= 12 ? "PM" : "AM";
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()} ${pad2(
    hour12
  )}:${pad2(d.getMinutes())}:00 ${ampm}`;
};

export default function CreateClinicalNote() {
  // ===== Envelope meta =====
  const [patient, setPatient] = useState({
    patientId: "129807",
    profileId: "3",
  });

  const [meta, setMeta] = useState({
    templateName: "_Hospital Discharge Follow Up",
    comment: "Nota de prueba vía API (todos los campos)",
    noteDatetime: toUsDatetime(new Date()),
    msgtime: toUsDatetime(new Date()),
    nocookie: "0",
  });

  // ===== Sections =====
  const [location, setLocation] = useState({
    location: "Main Clinic",
    location2: "Suite 200",
    location1: "Room 3",
  });

  const [reason, setReason] = useState({
    reasonForAppointment: "ER Discharge Follow Up",
  });

  const [vitals, setVitals] = useState({
    weight: 180,
    height: 70,
    bmi: 25.8,
    temperature: 98.6,
    pulse: 76,
    o2SatRa: 98,
    bpPrevSys: 120,
    bpPrevDia: 80,
    bp1Sys: 120,
    bp1Dia: 80,
    bpOption1: 3, // 1=Sitting, 2=Standing, 3=Supine (ajusta si tu vendor usa otro mapping)
    bpLocation1: 2, // 1=Left, 2=Right
  });

  // Auto-BMI
  useEffect(() => {
    if (vitals.height > 0 && vitals.weight > 0) {
      const bmi =
        Math.round(((vitals.weight / (vitals.height * vitals.height)) * 703) * 10) /
        10;
      if (bmi !== vitals.bmi) setVitals((v) => ({ ...v, bmi }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vitals.height, vitals.weight]);

  const [hpi, setHpi] = useState({
    general: "Paciente pos-alta hospitalaria, acude para control.",
    tcm:
      "TCM: Type of admission: ER; Admission: 2025-09-25; Discharge: 2025-09-27; Discharge Dx: Pneumonia; Meds reconciled: Yes; Discharge summary in chart: Yes; Complexity: Moderate.",
  });

  const [meds, setMeds] = useState({
    allReviewedAndReconciled: true,
    text: "Amoxicillin 500 mg q8h x7 days.",
  });

  const [pmh, setPmh] = useState({
    noChangeReviewed: true,
    text: "HTN controlled; DM2 without complications.",
  });

  // ===== ROS defaults =====
  const [ros, setRos] = useState({
    Constitutional: 1,
    chills: 0,
    fever: 0,
    lightheadedness: 0,
    ChangesinAppetite: 0,
    Fatigue: 0,
    WeightChange: 0,

    Ophthalmologic: 1,
    VisionChanges: 0,
    Doublevision: 0,
    EyePain: 0,

    ENT: 1,
    Decreasedhearing: 0,
    Ringingintheears: 0,
    EarPain: 0,
    Nose1: 0,
    NoseBleed: 0,
    NasalCongestion: 0,
    sinuspain: 0,
    Throat1: 0,
    SoreThroat: 0,
    Difficultyswallowing: 0,

    Endocrine: 1,
    Coldintolerance: 0,
    Excessivethirst: 0,
    frequenturination: 0,
    Heatintolerance: 0,
    excessivesweating: 0,

    Respiratory: 1,
    Shortnessofbreath: 0,
    Wheezing: 0,
    Hemoptysis: 0,
    Cough: 0,
    sputumproduction: 0,

    Cardiovascular: 1,
    Chestpain: 0,
    ChestpressureorChestdiscomfort: 0,
    Palpitations: 0,
    Irregularheartbeat: 0,
    difficultylayingflat: 0,
    dyspneaonexertion: 0,
    Claudication: 0,

    Gastrointestinal: 1,
    Abdominalpain: 0,
    Nausea: 0,
    vomitting: 0,
    changeinbowelhabits: 0,
    anorexia: 0,
    BloodinStool: 0,
    Diarrhea: 0,
    Heartburn: 0,

    Genitourinary: 1,
    bloodintheurine: 0,
    difficultyurinating: 0,
    paininthelowerback: 0,

    Musculoskeletal1: 1,
    painfuljoints: 0,
    Swollenjoints: 0,
    Cervical: 0,
    Thoraxic: 0,
    Lumbar: 0,

    Neurologic1: 1,
    Paralysis: 0,
    Seizures: 0,
    TinglingNumbness: 0,
    Dizziness1: 0,
    weakness: 0,
    Newonsetheadache: 0,
    Difficultyspeaking: 0,
    Headache: 0,
    Dizziness: 0,
    lossofstrength: 0,
    Memoryloss: 0,
    abnormality: 0,

    Psy: 1,
    Suicidalhomicidalthoughts: 0,
    DifficultySleeping: 0,
  });

  // ===== Physical Examination =====
  const [exam, setExam] = useState({
    GeneralAppearance: { on: 1, text: "Alert and oriented x3; no acute distress." },
    EyesBL: { on: 1, text: "BL sclera anicteric, PERRLA, EOMI." },
    Ears: { on: 1, text: "Hearing grossly intact; canals and TMs normal." },
    Nose: { on: 1, text: "Nares patent, no discharge." },
    OralCavity: { on: 1, text: "Moist mucosa, no lesions." },
    Throat: { on: 1, text: "No erythema/exudate; uvula midline." },
    NeckThyroid: { on: 1, text: "Supple; no tenderness, LAD, or thyromegaly." },
    Heart: { on: 1, text: "RRR, S1/S2 normal; no murmurs." },
    Lungs: { on: 1, text: "CTAB, good air movement; no wheezes/rales/rhonchi." },
    Breast: { on: 1, text: "" },
    Abdomen: { on: 1, text: "Soft, NT/ND, BS+, no guarding or organomegaly." },
    Pelvic: { on: 1, text: "" },
    back: { on: 1, text: "Ambulates without assistance; no deformity." },
    Musculoskeletal: { on: 1, text: "No swelling or deformity; strength 5/5." },
    Skin: { on: 1, text: "Visible skin clear; no rash or abnormal moles." },
    Neurologic: { on: 1, text: "A&O x3; motor/sensation intact; normal gait; no tremor." },
    Extremities: { on: 1, text: "No cyanosis, clubbing, or edema." },
    Psychiatric: { on: 1, text: "Normal mood and affect." },
  });

  const [phq2, setPhq2] = useState({ q1: 0, q2: 0, reviewedPhq9: 0 });

  const [assessPlanFu, setAssessPlanFu] = useState({
    assessments: "Favorable post-discharge evolution; no complications.",
    treatment:
      "Discharge summary & meds reviewed; education provided; phone follow-up in 48h.",
    followUp: "Follow-up in 2 weeks; call earlier if worsens.",
  });

  const onSave = async () => {
    const nowStr = toUsDatetime(new Date());

    const payload = buildEhrNotePayload({
      patient,
      meta: { ...meta, msgtime: nowStr, noteDatetime: nowStr },
      location,
      reason,
      vitals,
      hpi,
      meds,
      pmh,
      ros,
      exam,
      phq2,
      assessPlanFu,
    });

    console.log("EHR NOTE PAYLOAD →", JSON.stringify(payload, null, 2));

    try {
      // Llamada a TU backend (sin tokens en el front)
      const result = await addEhrNote(payload);
      // result típico: { id, createdate, rawXml }
      if (result?.id) {
        alert(`Note created! ID: ${result.id}\nCreateDate: ${result.createdate ?? ""}`);
      } else {
        alert("Note created, revisar detalle en consola.");
      }
      console.log("Backend result →", result);
    } catch (err) {
      console.error("Error posting EHR note:", err);
      alert("Error posting EHR note. Revisa consola para detalles.");
    }
  };

  const onCancel = () => window.history.back();

  const pageState = useMemo(
    () => ({
      patient,
      meta,
      location,
      reason,
      vitals,
      hpi,
      meds,
      pmh,
      ros,
      exam,
      phq2,
      assessPlanFu,
    }),
    [patient, meta, location, reason, vitals, hpi, meds, pmh, ros, exam, phq2, assessPlanFu]
  );

  return (
    <div className="cn-container">
      <h2>Create Clinical Note</h2>

      {/* Header (solo Patient ID, Profile ID, Comments) */}
      <div className="cn-card">
        <div className="row">
          <div>
            <label>Patient ID</label>
            <input
              value={patient.patientId}
              onChange={(e) => setPatient((p) => ({ ...p, patientId: e.target.value }))}
            />
          </div>
          <div>
            <label>Profile ID</label>
            <input
              value={patient.profileId}
              onChange={(e) => setPatient((p) => ({ ...p, profileId: e.target.value }))}
            />
          </div>
          <div>
            <label>Comments</label>
            <input
              value={meta.comment}
              onChange={(e) => setMeta((m) => ({ ...m, comment: e.target.value }))}
            />
          </div>
        </div>
      </div>

      <LocationForm
        location={location}
        reason={reason}
        onChangeLocation={setLocation}
        onChangeReason={setReason}
      />

      <VitalsForm vitals={vitals} onChange={setVitals} />

      <HpiForm
        hpi={hpi}
        meds={meds}
        pmh={pmh}
        onChangeHpi={setHpi}
        onChangeMeds={setMeds}
        onChangePmh={setPmh}
      />

      <RosForm ros={ros} onChange={setRos} />

      <ExamForm exam={exam} onChange={setExam} />

      <Phq2Form phq2={phq2} onChange={setPhq2} />

      <AssessmentsPlanFollowUp data={assessPlanFu} onChange={setAssessPlanFu} />

      <ActionsBar onSave={onSave} onCancel={onCancel} statePreview={pageState} />
    </div>
  );
}
